import { CSSProperties, CSSRule, CSSObject, CustomMedia } from '@gsc/types'
import { resolveMedia } from '../../helpers/resolve-media'

const HYPHENATE_REGEX = /[A-Z]|^ms/g

interface Node {
  css: CSSObject
  selector: string
  media?: string
}

interface ParsedRule {
  cssText: string
  selector: string
  media?: string
}

function memoize<V>(fn: (arg: string) => V): (arg: string) => V {
  const cache = Object.create(null)
  return (arg: string) => {
    if (cache[arg] === undefined) {
      cache[arg] = fn(arg)
    }
    return cache[arg]
  }
}

const isCustomProperty = (name: string) => name.charCodeAt(1) === 45

const vendorPrefixedNames = (name: string): string[] => {
  return name === 'backdrop-filter' ? [name, `-webkit-${name}`] : [name]
}

const processName = memoize((name: string) => {
  return isCustomProperty(name) ? name : name.replace(HYPHENATE_REGEX, '-$&').toLowerCase()
})

function serializeProperties(props: CSSProperties): string {
  let res = ''
  for (const key in props) {
    let value = props[key as keyof typeof props]
    if (typeof value === 'string' && !value) {
      value = `''`
    }

    res += vendorPrefixedNames(processName(key))
      .map(name => `${name}:${value};`)
      .join('')
  }

  return res
}

// Combining expressions with multiple comma-separated queries
// e.g. '@media (min-height: 680px), screen and (orientation: portrait)' isn't supported
function combineMediaFeatures(parentMedia: string, childMedia: string): string {
  const query = [parentMedia, childMedia]
    .map((m = '') => m.replace('@media', '').trim())
    .filter(Boolean)
    .join(' and ')

  return query ? `@media ${query}` : ''
}

function createChildNode(key: string, css: CSSObject, parentNode: Node): Node {
  const { selector, media } = parentNode
  const isMedia = key.startsWith('@media')
  if (isMedia) {
    return {
      media: combineMediaFeatures(media, key),
      selector,
      css,
    }
  }
  // else consider key a nested selector
  const childSelector = key.includes('&') ? key.replace(/&/g, selector) : `${selector} ${key}`
  return {
    selector: childSelector.trim(),
    media,
    css,
  }
}

// sort rules to have at-rules (media queries) at the end
function sortRules(rules: ParsedRule[]): ParsedRule[] {
  return [...rules].sort((a, b) => {
    if (a.media && !b.media) return 1
    if (!a.media && b.media) return -1
    return 0
  })
}

function createRules(node: Node): ParsedRule[] {
  const { selector, css, media = '' } = node
  const currentProps: { [name: string]: string | number } = {}
  const nodes: Node[] = []

  for (const key in css) {
    const value = css[key as keyof typeof css]
    if (typeof value === 'object') {
      nodes.push(createChildNode(key, value, node))
    } else {
      currentProps[key] = value
    }
  }

  const rules: ParsedRule[] = []
  const cssText = serializeProperties(currentProps)
  if (cssText) {
    rules.push({ cssText, selector, media })
  }

  rules.push(...nodes.flatMap(createRules))
  return sortRules(rules)
}

function serializeCssStyleRule(
  { selector = '', css = {} }: { selector?: string; css?: CSSObject },
  customMedia: CustomMedia = {},
): string {
  const rules = createRules({ css, selector })
  let lastMedia = ''
  let res = ''

  for (const r of rules) {
    if (lastMedia !== r.media) {
      if (lastMedia) {
        res += '}'
      }
      if (r.media) {
        res += `${resolveMedia(r.media, customMedia)} {`
      }
      lastMedia = r.media
    }

    res += `${r.selector}{${r.cssText}}`
  }

  if (lastMedia) {
    res += '}'
  }

  return res
}

function serializeCssAtRule({
  name = '',
  params = '',
  css,
}: {
  name?: string
  params?: string
  css?: CSSObject
}): string {
  if (name.includes('keyframes')) {
    const cssText = serializeCssStyleRule({ selector: '', css })
    return `@keyframes ${params}{${cssText}}`
  } else if (name.includes('import') && params) {
    return `@import url('${params}');`
  }

  return ''
}

export function serializeCssRule(rule: CSSRule, customMedia: CustomMedia = {}): string {
  return rule.name ? serializeCssAtRule(rule) : serializeCssStyleRule(rule, customMedia)
}
